import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: any) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        logger.error('Error getting initial session', { error: error.message });
      } else {
        setSession(session);
        setUser(session?.user ?? null);
        logger.info('Initial session loaded', { userId: session?.user?.id });
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.info('Auth state changed', { event, userId: session?.user?.id });
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Handle different auth events
      switch (event) {
        case 'SIGNED_IN':
          logger.info('User signed in', { userId: session?.user?.id });
          break;
        case 'SIGNED_OUT':
          logger.info('User signed out');
          // Clear any cached user data
          localStorage.removeItem('userGender');
          localStorage.removeItem('userStyles');
          localStorage.removeItem('userBodyType');
          localStorage.removeItem('userMeasurements');
          localStorage.removeItem('userFacialFeatures');
          localStorage.removeItem('userColorSeason');
          break;
        case 'PASSWORD_RECOVERY':
          logger.info('Password recovery initiated');
          break;
        case 'TOKEN_REFRESHED':
          logger.debug('Auth token refreshed');
          break;
        case 'USER_UPDATED':
          logger.info('User profile updated', { userId: session?.user?.id });
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });

      if (error) {
        logger.error('Sign up failed', { error: error.message, email });
      } else {
        logger.info('Sign up successful', { email });
      }

      return { error };
    } catch (error) {
      logger.error('Sign up error', { error });
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logger.error('Sign in failed', { error: error.message, email });
      } else {
        logger.info('Sign in successful', { email });
      }

      return { error };
    } catch (error) {
      logger.error('Sign in error', { error });
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();

      if (error) {
        logger.error('Sign out failed', { error: error.message });
      } else {
        logger.info('Sign out successful');
      }

      return { error };
    } catch (error) {
      logger.error('Sign out error', { error });
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        logger.error('Password reset failed', { error: error.message, email });
      } else {
        logger.info('Password reset email sent', { email });
      }

      return { error };
    } catch (error) {
      logger.error('Password reset error', { error });
      return { error: error as AuthError };
    }
  };

  const updateProfile = async (updates: any) => {
    try {
      const { error } = await supabase.auth.updateUser({
        data: updates
      });

      if (error) {
        logger.error('Profile update failed', { error: error.message });
      } else {
        logger.info('Profile updated successfully');
      }

      return { error };
    } catch (error) {
      logger.error('Profile update error', { error });
      return { error: error as AuthError };
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}